import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/shared/components/ui/Modal';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Button } from '@/shared/components/ui/Button';
import { useToast } from '@/shared/components/ui/Toast';
import { useUpdateDashboardSettings } from '../hooks/useAdmin';
import type { DashboardSettings } from '../services/dashboardSettingsService';

const dashboardSettingsSchema = z.object({
  overdue_ordered_yellow_days: z.number().min(1).max(365),
  overdue_ordered_red_days: z.number().min(1).max(365),
  stale_pending_yellow_days: z.number().min(1).max(365),
  stale_pending_red_days: z.number().min(1).max(365),
  receiving_backlog_days: z.number().min(1).max(365),
  usage_yellow_pct: z.number().min(0).max(100),
  usage_red_pct: z.number().min(0).max(100),
  ordering_day_of_week: z.number().min(0).max(6),
  enable_overdue_ordered: z.boolean(),
  enable_stale_pending: z.boolean(),
  enable_receiving_backlog: z.boolean(),
  enable_usage_alert: z.boolean(),
});

type DashboardSettingsFormData = z.infer<typeof dashboardSettingsSchema>;

interface DashboardSettingsFormProps {
  isOpen: boolean;
  onClose: () => void;
  settings?: DashboardSettings;
  locationId: string;
}

export function DashboardSettingsForm({
  isOpen,
  onClose,
  settings,
  locationId,
}: DashboardSettingsFormProps) {
  const { showToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<DashboardSettingsFormData>({
    resolver: zodResolver(dashboardSettingsSchema),
  });

  const updateMutation = useUpdateDashboardSettings();

  useEffect(() => {
    if (isOpen && settings) {
      reset({
        overdue_ordered_yellow_days: settings.overdue_ordered_yellow_days,
        overdue_ordered_red_days: settings.overdue_ordered_red_days,
        stale_pending_yellow_days: settings.stale_pending_yellow_days,
        stale_pending_red_days: settings.stale_pending_red_days,
        receiving_backlog_days: settings.receiving_backlog_days,
        usage_yellow_pct: settings.usage_yellow_pct,
        usage_red_pct: settings.usage_red_pct,
        ordering_day_of_week: settings.ordering_day_of_week,
        enable_overdue_ordered: settings.enable_overdue_ordered,
        enable_stale_pending: settings.enable_stale_pending,
        enable_receiving_backlog: settings.enable_receiving_backlog,
        enable_usage_alert: settings.enable_usage_alert,
      });
    }
  }, [isOpen, settings, reset]);

  const onSubmit = async (data: DashboardSettingsFormData) => {
    try {
      await updateMutation.mutateAsync({
        location_id: locationId,
        ...data,
      });
      showToast('Dashboard settings updated successfully', 'success');
      onClose();
    } catch (error) {
      console.error('Error updating dashboard settings:', error);
      showToast(
        error instanceof Error
          ? error.message
          : 'Failed to update dashboard settings',
        'error'
      );
    }
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Dashboard Settings"
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Overdue Ordered Frames */}
        <div className="border-t pt-4">
          <div className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              id="enable_overdue_ordered"
              {...register('enable_overdue_ordered')}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor="enable_overdue_ordered"
              className="text-sm font-medium text-gray-900"
            >
              Enable Overdue Ordered Frames Alert
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-6">
            <Input
              label="Yellow Alert (days)"
              type="number"
              required
              error={errors.overdue_ordered_yellow_days?.message}
              {...register('overdue_ordered_yellow_days', {
                valueAsNumber: true,
              })}
            />
            <Input
              label="Red Alert (days)"
              type="number"
              required
              error={errors.overdue_ordered_red_days?.message}
              {...register('overdue_ordered_red_days', { valueAsNumber: true })}
            />
          </div>
        </div>

        {/* Stale Pending Frames */}
        <div className="border-t pt-4">
          <div className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              id="enable_stale_pending"
              {...register('enable_stale_pending')}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor="enable_stale_pending"
              className="text-sm font-medium text-gray-900"
            >
              Enable Stale Pending Frames Alert
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-6">
            <Input
              label="Yellow Alert (days)"
              type="number"
              required
              error={errors.stale_pending_yellow_days?.message}
              {...register('stale_pending_yellow_days', {
                valueAsNumber: true,
              })}
            />
            <Input
              label="Red Alert (days)"
              type="number"
              required
              error={errors.stale_pending_red_days?.message}
              {...register('stale_pending_red_days', { valueAsNumber: true })}
            />
          </div>
        </div>

        {/* Receiving Backlog */}
        <div className="border-t pt-4">
          <div className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              id="enable_receiving_backlog"
              {...register('enable_receiving_backlog')}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor="enable_receiving_backlog"
              className="text-sm font-medium text-gray-900"
            >
              Enable Receiving Backlog Alert
            </label>
          </div>
          <div className="pl-6">
            <Input
              label="Threshold (days)"
              type="number"
              required
              error={errors.receiving_backlog_days?.message}
              {...register('receiving_backlog_days', { valueAsNumber: true })}
            />
          </div>
        </div>

        {/* Usage Alert */}
        <div className="border-t pt-4">
          <div className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              id="enable_usage_alert"
              {...register('enable_usage_alert')}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor="enable_usage_alert"
              className="text-sm font-medium text-gray-900"
            >
              Enable Usage Alert
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-6">
            <Input
              label="Yellow Alert (%)"
              type="number"
              required
              error={errors.usage_yellow_pct?.message}
              {...register('usage_yellow_pct', { valueAsNumber: true })}
            />
            <Input
              label="Red Alert (%)"
              type="number"
              required
              error={errors.usage_red_pct?.message}
              {...register('usage_red_pct', { valueAsNumber: true })}
            />
          </div>
        </div>

        {/* Ordering Day */}
        <div className="border-t pt-4">
          <Select
            label="Preferred Ordering Day"
            required
            options={[
              { value: '0', label: 'Sunday' },
              { value: '1', label: 'Monday' },
              { value: '2', label: 'Tuesday' },
              { value: '3', label: 'Wednesday' },
              { value: '4', label: 'Thursday' },
              { value: '5', label: 'Friday' },
              { value: '6', label: 'Saturday' },
            ]}
            error={errors.ordering_day_of_week?.message}
            {...register('ordering_day_of_week', { valueAsNumber: true })}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
